import { useEffect, useState } from 'react';

import type { NotificationMessage } from '../../types/entities';

import { DRIFTSMELDING_COOKIE_EXPIRES_DAYS, DRIFTSMELDING_COOKIE_NAME } from '@helsenorge/core-cms/info-eller-driftsmelding/cookie';
import type LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { getCookieValue, getSuffix } from '@helsenorge/framework-utils/cookie';
import { useSetLanguageEvent } from '@helsenorge/framework-utils/hn-language';
import { getPath } from '@helsenorge/framework-utils/hn-page';
import { erTjenester } from '@helsenorge/framework-utils/hn-proxy-service';
import { warn } from '@helsenorge/framework-utils/logger';
import { deleteSharedCookie, setSharedCookie } from '@helsenorge/framework-utils/shared-cookies';
import { HeaderFooterEvents } from '@helsenorge/framework-utils/web-component/constants';
import useWebComponentEvents from '@helsenorge/framework-utils/web-component/useWebComponentEvents';

import { getContentForContentReference, getContentForPath, type IAdditionalPageContent } from './get-additionalPageContent';

interface ClosedMessage {
  contentId: string;
  timestamp: number;
}

/**
 * Formaterer json til et lightweight cookie format
 * Fra [{"contentId":"123","timestamp":1638464584}, {"contentId":"456","timestamp":1638464584}]
 * Til 123:1638464584|456:1638464584
 * @param closedMessages Liste med lukkede meldinger
 * @returns Lukkede meldinger formattert for cookie
 */
export const formatJsonCookie = (closedMessages: ClosedMessage[]): string => {
  if (closedMessages.length === 0) {
    return '';
  }
  const json = JSON.stringify(closedMessages);
  let formattedJson = json.replace(/},{/g, '|');
  formattedJson = formattedJson.replace(/contentId|timestamp|"|\[|{|}|]|:/g, '');

  return formattedJson.replace(/,/g, ':');
};

/**
 * Reverserer formatering av lightweight cookie
 * @param formattedJson Innhold i cookie
 * @returns Liste med lukkede meldinger
 */
export const reverseFormatJsonCookie = (formattedJson: string): ClosedMessage[] => {
  if (formattedJson.length === 0) {
    return [];
  }
  const distinctList: Array<ClosedMessage> = [];

  formattedJson.split('|').forEach(item => {
    const splitItem = item.split(':');
    return distinctList.push({
      contentId: splitItem[0],
      timestamp: Number(splitItem[1]),
    });
  });

  return distinctList;
};

/**
 * Hent lukkede meldinger fra cookie
 * @returns Liste med lukkede meldinger
 */
const getClosedMessages = (): ClosedMessage[] => {
  try {
    const closedDriftsmeldingerJson = getCookieValue(DRIFTSMELDING_COOKIE_NAME);

    if (typeof closedDriftsmeldingerJson === 'string') {
      return reverseFormatJsonCookie(closedDriftsmeldingerJson);
    }
    return [];
  } catch (error) {
    deleteSharedCookie(getSuffix(DRIFTSMELDING_COOKIE_NAME));

    warn('Cleared closed driftsmelding cookie', error);
    return [];
  }
};

/**
 * Skjuler infomeldinger på åpne sider, der de vises serversiderendret i stedet
 * @param message Drifts- eller infomelding
 * @returns true dersom melding skal vises
 */
const filterInfomeldinger = (message: NotificationMessage): boolean => erTjenester() || message.messageType !== 'infomelding';

/**
 * Skjuler lukkede meldinger
 * @param message Melding som skal sjekkes
 * @param closedMessages Liste med meldinger som er lukket
 * @returns true dersom meldingen skal vises
 */
const filterClosedMessages = (message: NotificationMessage, closedMessages: ClosedMessage[]): boolean =>
  !closedMessages.find(closed => closed.contentId === message.id && closed.timestamp === message.unixTimeStamp);

/**
 * Dedupliserer liste med lukkede meldinger slik at de ikke lagres dobbelt
 * @param unique Liste med unike lukkede meldinger
 * @param melding Melding som skal sjekkes
 * @returns Liste med unike lukkede meldinger
 */
const toUniqueClosedMessages = (unique: ClosedMessage[], closedMessage: ClosedMessage): ClosedMessage[] =>
  !unique.some(x => x.contentId === closedMessage.contentId) ? [...unique, closedMessage] : unique;

/**
 * Sorter meldinger etter timestamp
 * @param a Melding
 * @param b Melding
 * @returns > 0 dersom a skal sorteres etter b, < 0 dersom a skal sorteres før b, eventuelt 0 om de er like
 */
const sortByTimestamp = (a: ClosedMessage, b: ClosedMessage): number => b.timestamp - a.timestamp;

/**
 * Hent driftsmeldinger og infomeldinger
 * @param contentReference Side-ID i CMS (trengs kun på åpne sider)
 * @param languageCode Språkkode
 * @returns Liste med meldinger og callback for å lagre at meldinger lukkes
 */
export const useAdditionalPageContent = (
  ref: React.RefObject<HTMLElement | null>,
  contentReference?: string,
  language?: LanguageLocales
): [NotificationMessage[], (message: NotificationMessage) => void] => {
  const { subscribe } = useWebComponentEvents('hn-webcomp-driftspanel', ref);
  const [currentLanguage, setCurrentLanguage] = useState(language);
  const [path, setPath] = useState<string>();
  const [closedMessages, setClosedMessages] = useState<ClosedMessage[]>([]);
  const [content, setContent] = useState<IAdditionalPageContent>();

  useEffect(() => {
    const storedClosedMessages = getClosedMessages();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- @todo: Refaktorere ifbm med å hente meldinger fra additionalPageContent V2
    setClosedMessages(storedClosedMessages);

    return subscribe(HeaderFooterEvents.setdriftsmeldingpath, (event: CustomEvent) => {
      setPath(event.detail.path);
    });
  }, []);

  useEffect(() => {
    if (contentReference) {
      getContentForContentReference(contentReference, currentLanguage)
        .then(data => data && setContent(data))
        .catch(); // Feil håndteres i cms-content-api-service
    } else {
      getContentForPath(path || getPath() || '', currentLanguage)
        .then(data => data && setContent(data))
        .catch(); // Feil håndteres i cms-content-api-service
    }
  }, [contentReference, path, currentLanguage]);

  const handleUserLanguageEvent = (newLanguage: LanguageLocales): void => {
    if (erTjenester()) {
      setCurrentLanguage(newLanguage);
    }
  };

  useSetLanguageEvent(handleUserLanguageEvent);

  const handleClosed = ({ id: contentId, unixTimeStamp: timestamp }: NotificationMessage): void => {
    const prevClosed = getClosedMessages() ?? [];
    prevClosed.push({ contentId, timestamp });
    prevClosed.sort(sortByTimestamp);
    const unique = prevClosed.reduce(toUniqueClosedMessages, [] as ClosedMessage[]);

    setSharedCookie(getSuffix(DRIFTSMELDING_COOKIE_NAME), formatJsonCookie(unique), DRIFTSMELDING_COOKIE_EXPIRES_DAYS);
    setClosedMessages(unique);
  };

  const messages = content?.messages.filter(filterInfomeldinger).filter(message => filterClosedMessages(message, closedMessages)) ?? [];

  return [messages, handleClosed];
};
